const ITEM_CANCELLED_KEY = 'items_cancelled';
const ITEM_CREATED_KEY = 'items_created';
const PAYMENTS_CREATED_KEY = 'payments_created';
const PASSENGER_ID_PREFIX = 'passenger_';
const CHECKED_IN_TYPES = ['PASSAGE', 'LIGHT'];

export const shouldDisplayReasons = (action, data) =>
  [ITEM_CREATED_KEY, ITEM_CANCELLED_KEY, PAYMENTS_CREATED_KEY].includes(action) && data?.overrideReason?.length > 0;

export const capitalizeReason = (reason) => reason?.charAt(0)?.toUpperCase() + reason?.slice(1) || '';

export const buildReasons = (reason = {}) => {
  const reasonKey = capitalizeReason(reason?.key);

  return reasonKey.length > 0 ? `${reasonKey?.split('_')?.join(' ')}: ${reason?.value}` : '';
};

export const getDisplayPassengerId = (passengerId) => {
  if (!passengerId) {
    return '-';
  }

  const passengerIdString = `${passengerId}`;
  return passengerIdString.startsWith(PASSENGER_ID_PREFIX) ? passengerIdString.replace(PASSENGER_ID_PREFIX, '') : passengerIdString;
};

export const getBookingPassengerByUuid = (booking, passengerUuid) =>
  booking?.passengers?.find((passenger) => passenger.uuid === passengerUuid);

export const getBookingPassengerById = (booking, passengerId) => booking?.passengers?.find((passenger) => passenger.id === passengerId);

const getBookingItems = (booking) => booking?.allBookingItems ?? [];

const getBookingPassengerForBookingItem = (booking, bookingItem) =>
  bookingItem?.passenger || getBookingPassengerById(booking, bookingItem?.passengerId);

export const getBookingPassengerForHistoryTicket = (booking, ticket = {}) =>
  getBookingPassengerForBookingItem(
    booking,
    getBookingItems(booking).find(
      (bookingItem) =>
        (ticket.uuid && bookingItem.uuid === ticket.uuid) ||
        (ticket.ticketNo && [bookingItem.ticketNumber, bookingItem.externalTicketNumber].includes(ticket.ticketNo))
    )
  );

export const isPassageValidation = (revisionContents, tickets = [], action) => {
  const uuids = revisionContents?.[action] || [];
  if (!uuids.length) return true;
  const matched = tickets.filter((t) => uuids.includes(t.uuid));
  return matched.length === 0 || matched.every((t) => CHECKED_IN_TYPES.includes(t.validationType));
};

export const getActionName = (revisionContents = {}, actionType, tickets = []) => {
  const { __typename: _, ...revisions } = revisionContents;

  const formattedRevisions = {};
  for (let [key, value] of Object.entries(revisions)) {
    if (key === 'first_confirmation' || key === 'customer_updated') {
      formattedRevisions[key] = Boolean(value);
      continue;
    }

    formattedRevisions[key] = !!value && value?.length > 0;
  }

  const {
    first_confirmation,
    customer_updated,
    passengers_created,
    passengers_updated,
    items_created,
    items_cancelled,
    items_updated,
    payments_created,
    payments_updated,
    validations_created,
    validations_cancelled
  } = formattedRevisions;

  switch (true) {
    case first_confirmation === true:
      return 'TICKETS CONFIRMED';
    case !first_confirmation &&
      customer_updated &&
      passengers_created &&
      items_created &&
      !items_cancelled &&
      !payments_created &&
      !payments_updated:
      return 'TICKETS PUT ON-HOLD WITHOUT DEPOSIT';
    case !first_confirmation &&
      customer_updated &&
      passengers_created &&
      items_created &&
      !items_cancelled &&
      payments_created &&
      !payments_updated:
      return 'TICKETS PUT ON-HOLD WITH DEPOSIT';
    case !first_confirmation && !passengers_created && (items_created || items_cancelled):
      return 'TICKETS AFTERSALES';
    case !first_confirmation && !customer_updated && passengers_created && items_created && !items_cancelled:
      return 'ADD PASSENGER';
    case !first_confirmation && !passengers_created && !passengers_updated && !items_cancelled && items_updated:
      return 'SEAT CHANGE';
    case !first_confirmation && !passengers_created && passengers_updated && !items_cancelled && !items_updated:
      return 'PASSENGER UPDATE';
    case !first_confirmation &&
      !passengers_created &&
      !passengers_updated &&
      !items_created &&
      !items_updated &&
      !items_cancelled &&
      (payments_created || payments_updated):
      return 'PAYMENT';
    case !first_confirmation &&
      !passengers_created &&
      !passengers_updated &&
      !items_created &&
      !items_updated &&
      !items_cancelled &&
      !payments_created &&
      !payments_updated &&
      validations_created &&
      !validations_cancelled:
      return isPassageValidation(revisionContents, tickets, 'validations_created') ? 'TICKETS CHECKED IN' : 'TICKETS VALIDATED';
    case !first_confirmation &&
      !passengers_created &&
      !passengers_updated &&
      !items_created &&
      !items_updated &&
      !items_cancelled &&
      !payments_created &&
      !payments_updated &&
      !validations_created &&
      validations_cancelled:
      return isPassageValidation(revisionContents, tickets, 'validations_cancelled')
        ? 'TICKETS CHECKED OUT'
        : 'TICKETS WITH VALIDATION VOIDED';
    case actionType === 'AGENT_NOTE':
      return 'AGENT NOTE';
    case actionType === 'SCHEDULE_CHANGE':
      return 'SCHEDULE CHANGE';
    default:
      return 'OTHER';
  }
};
